import { useState } from "react";
import RowBlock from "../row-block/row-block";
import { RequirementColumn, RequirementFactory, Department } from '../../types/data';
import { arrowDouble } from "../../ui-icons/icons";
import {
  calculatesAverageValue,
  filteredAnswers,
  calculateIndentColumn,
  getDateAsString,
} from '../../utils/utils';
import { Report } from '../../types/table';
import { TooltipItem } from "src/utils/Tooltip";
import { getProductionDepartments } from "../../utils/data-table";

const COLUMNS = {
  '1': {
    width: '93px',
    indent: '0',
  },
  '2': {
    width: '93px',
    indent: '93px',
  },
  '3': {
    width: '93px',
    indent: '186px',
  },
  '4': {
    width: '170px',
    indent: '0',
    indentFactory: '279px',
  },
};

type ColumnBlockProps = RequirementColumn & {
  category: Report;
  dataColumn?: RequirementFactory[];
  departments: Department[];
  isCompleted?: boolean;
};

function ColumnBlock({
  title,
  columnRequirements,
  factory,
  year,
  month,
  completionDate,
  completedAssessment,
  category,
  dataColumn,
  departments,
  isCompleted
}: ColumnBlockProps) {
  const [isOpen, setIsOpen] = useState(false);

  const getClassName = (value: number | 'N/A') => {
    if (value === 'N/A') return 'bg-gray-200';
    if (value <= 59) return 'bg-pink-300';
    if (value >= 60 && value <= 69) return 'bg-yellow-300';
    if (value >= 70) return 'bg-green-300';
  };

  const handleToggleChange = () => {
    setIsOpen(!isOpen);
  };

  return (
    <tbody>
      <tr>
        {(category === 'factory' || category === 'region') && (
          <>
            <td
              className={`sticky border border-gray-400 bg-white px-2.5 py-2 text-xs before:absolute before:right-[-1px] before:top-0 before:block before:h-full before:w-[1px] before:bg-gray-400`}
              style={{
                left: `${COLUMNS['1'].indent}`,
                width: `${COLUMNS['1'].width}`,
              }}
            >
              {year}
            </td>

            <td
              className={`sticky border border-gray-400 bg-white px-2.5 py-2 text-xs before:absolute before:right-[-1px] before:top-0 before:block before:h-full before:w-[1px] before:bg-gray-400`}
              style={{
                left: `${COLUMNS['2'].indent}`,
                width: `${COLUMNS['2'].width}`,
              }}
            >
              {month}
            </td>

            {category === 'factory' && (
              <td
                className={`sticky border border-gray-400 bg-white px-2.5 py-2 text-xs before:absolute before:right-[-1px] before:top-0 before:block before:h-full before:w-[1px] before:bg-gray-400`}
                style={{
                  left: `${COLUMNS['3'].indent}`,
                  width: `${COLUMNS['3'].width}`,
                }}
              >
                {factory}
              </td>
            )}
          </>
        )}

        <td
          colSpan={category === 'factory' ? 1 : 4}
          onClick={handleToggleChange}
          className={`sticky border border-gray-400 bg-white px-2.5 py-2 text-xs after:absolute after:bottom-[-1px] after:left-0 after:block after:w-full after:h-[1px] after:bg-gray-400 before:absolute before:right-[-1px] before:top-0 before:block before:h-full before:w-[1px] before:bg-gray-400`}
          style={{
            left: calculateIndentColumn(category, COLUMNS['4']),
            width: `${COLUMNS['4'].width}`,
          }}
        >
          <span className="text-sm">{title}</span>
          <div
            className={`absolute right-3 top-[50%] translate-y-[-50%] ${isOpen && 'rotate-180'}`}
          >
            <TooltipItem
              className={`${isOpen ? 'rotate-180 translate-x-[-140%]' : ''}`}
              position="right"
              tooltipsText={isOpen ? 'Свернуть' : 'Развернуть'}
            >
              {arrowDouble}
            </TooltipItem>
          </div>
        </td>

        {category !== 'region' &&
          departments.map((el) => (
            <td
              key={el.id}
              className={`border border-gray-400 px-2.5 py-2 text-center text-xs ${getClassName(calculatesAverageValue(columnRequirements, el.title))}`}
            >
              {calculatesAverageValue(columnRequirements, el.title)}
              {calculatesAverageValue(columnRequirements, el.title) !== 'N/A' &&
                '%'}
            </td>
          ))}
        {category !== 'region' && (
          <>
            <td
              className={`border border-gray-400 px-2.5 py-2 text-center text-xs ${getClassName(calculatesAverageValue(
                filteredAnswers(columnRequirements, getProductionDepartments(departments)),
                undefined,
                true,
              ))}`}
            >
              {calculatesAverageValue(
                filteredAnswers(columnRequirements, getProductionDepartments(departments)),
                undefined,
                true,
              )}
              {calculatesAverageValue(
                filteredAnswers(columnRequirements, getProductionDepartments(departments)),
                undefined,
                true,
              ) !== 'N/A' && '%'}
            </td>

            <td
              className={`border border-gray-400 px-2.5 py-2 text-center text-xs ${getClassName(calculatesAverageValue(columnRequirements, undefined, true))}`}
            >
              {calculatesAverageValue(columnRequirements, undefined, true)}
              {calculatesAverageValue(columnRequirements, undefined, true) !==
                'N/A' && '%'}
            </td>
          </>
        )}

        {category === 'region' &&
          dataColumn &&
          dataColumn.map((el, index) => (
            <td
              key={index}
              className={`border border-gray-400 px-2.5 py-2 text-center text-xs ${getClassName(calculatesAverageValue(columnRequirements, undefined, undefined, el.shortTitle))}`}
            >
              {calculatesAverageValue(
                columnRequirements,
                undefined,
                undefined,
                el.shortTitle,
              )}
              {calculatesAverageValue(
                columnRequirements,
                undefined,
                undefined,
                el.shortTitle,
              ) !== 'N/A' && '%'}
            </td>
          ))}

        {category === 'region' && dataColumn && dataColumn.length > 1 && (
          <th
            className={`w-[116px] border border-gray-400 px-2.5 py-4 text-center text-xs font-medium ${getClassName(
              calculatesAverageValue(
                columnRequirements,
                undefined,
                undefined,
                undefined,
                undefined,
                true,
              ),
            )}`}
          >
            {calculatesAverageValue(
              columnRequirements,
              undefined,
              undefined,
              undefined,
              undefined,
              true,
            )}
            {calculatesAverageValue(
              columnRequirements,
              undefined,
              undefined,
              undefined,
              undefined,
              true,
            ) !== 'N/A' && '%'}
          </th>
        )}

        {category === 'factory' && (
          <>
            <td className="border border-gray-400 px-2.5 py-2 text-center text-xs">
              {completedAssessment}
            </td>

            <td className="border border-gray-400 px-2.5 py-2 text-center text-xs">
              {getDateAsString(completionDate as string)}
            </td>
          </>
        )}
      </tr>

      {isOpen &&
        columnRequirements.map((column, index) => (
          <RowBlock
            key={index}
            category={category}
            year={year}
            month={month}
            factory={factory}
            completedAssessment={completedAssessment}
            completionDate={completionDate}
            dataColumn={dataColumn as RequirementFactory[]}
            departments={departments}
            {...column}
            isCompleted
          />
        ))}
    </tbody>
  );
}

export default ColumnBlock;
